import { IGachaBannerReward, IGachaRollError, IGachaRollSuccess, TransactionStatus } from '~/types/gacha';
import { getAccountState, getTransaction, roll } from '.';
import store from '~/Store';
import { idleTracker, syncAccountInfo } from '~/sync';
import { logError } from '~/utils/logging';
import { POPUPS_NAME } from '~/components/PopupManager';
import ErrorPopup from '../Components/ErrorPopup/ErrorPopup';
import { isEmptyObject, isMobileOrTabletWindow } from '~/utils/utils';
import { getSupportedVideo } from '~/utils/video';
import { getPrimaryReward, getRecivedRewardsByNames } from '../utils/rewards';
import { gachaActions } from '~/Store/gachaSlice';
import { appActions } from '~/Store/appSlice';
import { IRewardItem } from '~/components/Popups/RewardsPopup/Reward/Reward';
import { IRewardPopupData } from '~/components/Popups/RewardsPopup/RewardsPopup';
import { allowedEntityTypesForRollAnim } from '../utils/helpers';
import { isDisabledAnimationByName } from '~/utils/animations/settings';
import { getPrimaryItem } from '~/utils/bundles';
import { t } from '~/utils/localization';
import { BUNDLE_DECORATION } from '~/types/bundle';
import { GameRarity } from '~/types/rarity';

class Processor {
    private TRANSACTIONS_SYNC_INTERVAL = 1;
    private ACCOUNT_STATE_SYNC_INTERVAL = 10;
    private isStartedAnimation = false;
    private successHandlers: Record<string, DefaultFunction> = {};

    private async waitForSeconds(seconds: number) {
        return new Promise((resolve) => {
            setTimeout(resolve, seconds * 1000);
        });
    }

    private showError(bundleName?: string) {
        if (bundleName) {
            store.dispatch(gachaActions.updateTransactions({ bundleName, transactionId: null }));
        }
        store.dispatch(
            appActions.changeVisibilityPopup({
                name: POPUPS_NAME.CUSTOM_COMPONENT,
                isVisible: true,
                data: { component: <ErrorPopup /> },
            }),
        );
    }

    public async roll(bundleName: string, quantity: number, onSuccess?: DefaultFunction) {
        try {
            store.dispatch(gachaActions.updateTransactions({ bundleName, transactionId: bundleName }));
            store.dispatch(gachaActions.updateAnimationState(true));
            const response = await roll(bundleName, quantity);
            store.dispatch(gachaActions.updateTransactions({ bundleName, transactionId: response.transactionId }));
            if (onSuccess) {
                this.successHandlers[bundleName] = onSuccess;
            }
            if (response?.transactionId) {
                this.getTransaction(response.transactionId);
            } else {
                this.showError(bundleName);
            }
        } catch (e) {
            this.showError(bundleName);
        }
    }

    private async updateAccountState() {
        const accountState = store.getState().gacha;
        const response = await getAccountState();
        if (!response || accountState?.ts > response.ts) {
            return;
        }

        store.dispatch(gachaActions.updateAccountState(response));
    }

    public async accountStateSync() {
        try {
            if (idleTracker.isUserActive()) {
                await this.updateAccountState();
            }
        } catch (e) {
            logError('[mystery] account sync error', e);
        }
        await this.waitForSeconds(this.ACCOUNT_STATE_SYNC_INTERVAL);
        this.accountStateSync();
    }

    private prepareReward(reward: IGachaBannerReward) {
        return {
            images: reward.images,
            rarity: reward.rarity || GameRarity.common,
            items: reward.items.map((item) => {
                return {
                    amount: item.amount,
                    customisation: item.customisation,
                    isUnique: item.isUnique,
                    isPrimary: item.isPrimary,
                    shipId: item.customisation?.shipId,
                    type: item.type,
                    compensation: item.compensation,
                    identifier: item.identifier,
                };
            }) as IItemCommonData[],
            name: reward.name,
            title: reward.title,
            backgroundColor: reward.backgroundColor,
            cashback: reward.cashback,
            label: reward.isExtraReward ? t('Extra reward') : null,
        };
    }

    private async onSuccessHandler(response: IGachaRollSuccess) {
        if (this.isStartedAnimation) {
            return;
        }

        await this.updateAccountState();
        await syncAccountInfo();

        const { banners } = store.getState().gacha;
        const currentBanner = banners[response.bundleName];
        const receivedRewards = getRecivedRewardsByNames(response.rewards, currentBanner);

        const valuableReward = receivedRewards[0];
        const primaryItem = getPrimaryReward(valuableReward.items) || valuableReward.items[0];
        store.dispatch(gachaActions.updateTransactions({ bundleName: response.bundleName, transactionId: null }));

        const rewardsItems: IRewardItem[] = receivedRewards.map((reward) => this.prepareReward(reward));

        store.dispatch(gachaActions.updateTransactions({ bundleName: response.bundleName, transactionId: null }));

        const showFinalPopup = () => {
            this.isStartedAnimation = false;
            store.dispatch(gachaActions.updateAnimationState(false));
            store.dispatch(
                appActions.changeVisibilityPopup({
                    name: POPUPS_NAME.REWARDS,
                    isVisible: true,
                    data: {
                        rewards: rewardsItems,
                        onCloseHandler: this.successHandlers[response.bundleName],
                        compensatedItemIds: response.compensatedItems,
                    } as IRewardPopupData,
                }),
            );
        };

        const showPromoRewards = async () => {
            for (let i = 0; i < receivedRewards.length; i++) {
                await new Promise((resolve) => {
                    const showRewardFullscreen = () => {
                        if (isMobileOrTabletWindow) {
                            resolve(1);
                            return;
                        }

                        if (!receivedRewards[i].images.big) {
                            resolve(1);
                            return;
                        }

                        const primaryItem = getPrimaryItem(receivedRewards[i].items);
                        if (!primaryItem) {
                            resolve(1);
                            return;
                        }

                        store.dispatch(
                            appActions.changeVisibilityPopup({
                                name: POPUPS_NAME.REWARD_PROMO_SCREEN,
                                isVisible: true,
                                data: {
                                    backgroundImage: receivedRewards[i].images.big,
                                    backgroundColor: receivedRewards[i].backgroundColor,
                                    backgroundVideo: receivedRewards[i].idleVideo,
                                    additionalDescription: receivedRewards[i].additionalDescription,
                                    showDisclaimer: receivedRewards[i].decoration?.includes(BUNDLE_DECORATION.DISCLAIMER_APPEARANCE_AND_DESCRIPTION),
                                    item: primaryItem,
                                    onClose: () => {
                                        resolve(1);
                                    },
                                },
                            }),
                        );
                    };

                    const supportedIntroVideo = !isMobileOrTabletWindow && !isEmptyObject(receivedRewards[i].introVideo) && getSupportedVideo(receivedRewards[i].introVideo);
                    if (supportedIntroVideo) {
                        store.dispatch(
                            appActions.startVideo({
                                url: supportedIntroVideo,
                                name: response.bundleName,
                                onFinish: () => {
                                    showRewardFullscreen();
                                },
                            }),
                        );
                    } else {
                        showRewardFullscreen();
                    }
                });
            }

            showFinalPopup();
        };

        if (isMobileOrTabletWindow) {
            showFinalPopup();
            return;
        }

        let rollAnimation = currentBanner.videoAnimation.common;
        if (allowedEntityTypesForRollAnim.includes(primaryItem.type)) {
            rollAnimation = currentBanner.videoAnimation[valuableReward.rarity as keyof typeof currentBanner.videoAnimation] || rollAnimation;
        }

        const supportedRollAnimationUrl = getSupportedVideo(rollAnimation);
        if (!supportedRollAnimationUrl || isDisabledAnimationByName(response.bundleName)) {
            showPromoRewards();
            return;
        }

        this.isStartedAnimation = true;

        store.dispatch(
            appActions.startVideo({
                url: supportedRollAnimationUrl,
                name: response.bundleName,
                onFinish: () => {
                    showPromoRewards();
                },
            }),
        );
    }

    private async processingTransaction(response: IGachaRollSuccess | IGachaRollError) {
        switch (response.status) {
            case TransactionStatus.success: {
                await this.onSuccessHandler(response);
                break;
            }

            default:
            case TransactionStatus.error: {
                this.showError(response.bundleName);
                break;
            }
        }
    }

    public async getTransaction(transactionId: string) {
        try {
            const response = await getTransaction(transactionId);
            if (!response || response.status === TransactionStatus.pending) {
                await this.waitForSeconds(this.TRANSACTIONS_SYNC_INTERVAL);
                this.getTransaction(transactionId);
                return;
            }
            this.processingTransaction(response as IGachaRollSuccess | IGachaRollError);
        } catch (e) {
            this.showError();
        }
    }
}

export const GachaBundleProcessor = new Processor();
